<script setup lang="ts">
import Skeleton from "primevue/skeleton";
import { computed, ref, toRefs } from "vue";
import { useRecommenderResponses } from "@/components/MyStudents/Submissions/composables/useRecommenderResponses";
import {
  buildRecommenderCards,
  getRequestedRecommenders,
} from "@/components/MyStudents/Submissions/letterOfRecommendation";
import type { RecommenderCard } from "@/components/MyStudents/Submissions/types";
import ArrowDown from "@/components/shared/icons/ArrowDown.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import RecommenderResponseCard from "./RecommenderResponseCard.vue";
import type { RecommenderResponsesProps } from "./types";

const props = withDefaults(defineProps<RecommenderResponsesProps>(), {
  applicationId: undefined,
  todoId: undefined,
  todoSubmissions: () => [],
});
const { applicationId, todoId, todoSubmissions } = toRefs(props);

const { responses, questionLabels, isLoading, isError } = useRecommenderResponses(applicationId, todoId);

const isExpanded = ref(true);

const cards = computed<RecommenderCard[]>(() =>
  buildRecommenderCards(getRequestedRecommenders(todoSubmissions.value), responses.value, questionLabels.value),
);

const toggleExpanded = (): void => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="bg-white shadow-md rounded-lg p-6 w-full text-indigo-base"
    data-testid="recommender-responses-section"
  >
    <div class="flex md:flex-row flex-col pb-4 border-gray-200 border-b-2">
      <button
        type="button"
        class="flex flex-row w-full items-center cursor-pointer gap-3 text-left"
        :aria-expanded="isExpanded"
        aria-controls="recommender-responses-body"
        @click="toggleExpanded"
      >
        <div class="flex items-center justify-center w-7 h-7">
          <ArrowDown v-if="isExpanded" data-testid="recommender-responses-arrow-down" />
          <ArrowRight v-else class="w-5 h-5" data-testid="recommender-responses-arrow-right" />
        </div>
        <div class="flex flex-col justify-center">
          <div class="font-bold text-base">Recommender Response(s)</div>
          <div class="text-xs" data-testid="recommender-responses-subheader">Most recent shown first</div>
        </div>
      </button>
    </div>
    <Transition
      enter-active-class="transition-all duration-100 ease-out"
      enter-from-class="translate-y-1"
      enter-to-class="translate-y-0"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-1"
    >
      <div v-show="isExpanded" id="recommender-responses-body" class="pt-4" data-testid="recommender-responses-body">
        <div v-if="isLoading" class="flex flex-col gap-4" data-testid="recommender-responses-loading">
          <Skeleton height="5rem" border-radius="0.5rem" />
          <Skeleton height="5rem" border-radius="0.5rem" />
        </div>
        <div
          v-else-if="isError"
          class="rounded-md border border-red-300 bg-error-100 px-4 py-3 text-13p font-semibold leading-snug text-error-900"
          data-testid="recommender-responses-error"
        >
          We couldn't load the recommender responses. Please refresh the page to try again.
        </div>
        <div
          v-else-if="!cards.length"
          class="p-[18px] rounded-lg border border-dashed border-gray-175 text-center text-13p font-medium text-gray-800"
          data-testid="recommender-responses-empty"
        >
          No recommenders requested yet.
        </div>
        <div v-else class="flex flex-col" data-testid="recommender-responses-list">
          <RecommenderResponseCard
            v-for="card in cards"
            :key="card.id"
            :card="card"
            class="border-b border-gray-175 py-6 first:pt-0 last:border-b-0 last:pb-0"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>
